import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, CardBody, Input, Table, Spinner, Badge, Button } from 'reactstrap';
import { Breadcrumbs } from '../../../AbstractElements';
import { Package } from 'react-feather';
import axios from 'axios';
import API_BASE_URL from '../../../Config/api';

const InventoryList = () => {
  const [inventory, setInventory] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  const fetchInventory = useCallback(async (page = 1, searchTerm = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        ...(searchTerm && { search: searchTerm })
      });

      const response = await axios.get(`${API_BASE_URL}/api/store-inventory/all?${params}`);
      const data = response.data;

      if (data.success) {
        setInventory(data.inventory);
        setStores(data.stores);
        setPagination(data.pagination);
      }
    } catch (err) {
      console.error('Error fetching inventory:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInventory(1, '');
  }, [fetchInventory]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchInventory(1, search);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [search, fetchInventory]);

  const handlePageChange = (newPage) => {
    fetchInventory(newPage, search);
  };

  const getStockBadge = (stock) => {
    if (stock === 0 || stock === null || stock === undefined) {
      return <Badge color="light" className="text-muted">0</Badge>;
    }
    if (stock <= 5) {
      return <Badge color="danger">{stock}</Badge>;
    }
    if (stock <= 20) {
      return <Badge color="warning">{stock}</Badge>;
    }
    return <Badge color="success">{stock}</Badge>;
  };

  return (
    <Fragment>
      <Breadcrumbs parent="Inventory" title="Stock Levels" mainTitle="Inventory" />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div className="d-flex align-items-center">
                    <Package className="me-2" size={24} />
                    <h5 className="mb-0">All SKUs Stock Levels</h5>
                  </div>
                  <div style={{ width: '300px' }}>
                    <Input
                      type="text"
                      placeholder="Search by title, SKU..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                </div>

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-2">Loading inventory...</p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table bordered hover className="table-sm">
                        <thead className="bg-light">
                          <tr>
                            <th style={{ minWidth: '200px' }}>Title</th>
                            <th style={{ minWidth: '120px' }}>SKU</th>
                            <th style={{ minWidth: '80px' }}>Colour</th>
                            <th style={{ minWidth: '80px' }}>Fabric</th>
                            <th style={{ minWidth: '60px' }}>Size</th>
                            {stores.map(store => (
                              <th key={store.id} className="text-center" style={{ minWidth: '80px' }}>
                                {store.name}
                              </th>
                            ))}
                            <th className="text-center" style={{ minWidth: '80px' }}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inventory.length === 0 ? (
                            <tr>
                              <td colSpan={6 + stores.length} className="text-center py-4">
                                No inventory found
                              </td>
                            </tr>
                          ) : (
                            inventory.map((item, idx) => (
                              <tr key={`${item.product_id}-${item.variant_sku}-${idx}`}>
                                <td>{item.title || '-'}</td>
                                <td>
                                  <code className="small">{item.variant_sku || item.product_sku || '-'}</code>
                                </td>
                                <td>{item.colour || '-'}</td>
                                <td>{item.fabric || '-'}</td>
                                <td>{item.size || '-'}</td>
                                {stores.map(store => (
                                  <td key={store.id} className="text-center">
                                    {getStockBadge(item[store.code] || 0)}
                                  </td>
                                ))}
                                <td className="text-center">
                                  <strong>{item.total_stock || 0}</strong>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="text-muted small">
                          Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                          {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                          {pagination.total} items
                        </div>
                        <div>
                          <Button
                            color="primary"
                            size="sm"
                            outline
                            className="me-2"
                            disabled={pagination.page === 1}
                            onClick={() => handlePageChange(pagination.page - 1)}
                          >
                            Previous
                          </Button>
                          <span className="mx-2">
                            Page {pagination.page} of {pagination.totalPages}
                          </span>
                          <Button
                            color="primary"
                            size="sm"
                            outline
                            className="ms-2"
                            disabled={pagination.page === pagination.totalPages}
                            onClick={() => handlePageChange(pagination.page + 1)}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default InventoryList;
